import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faDollarSign, 
  faBed, 
  faBath,
  faMapMarkerAlt,
  faCalendar,
  faSave,
  faKey,
  faFire,
  faRuler,
  faSwimmer,
  faWarehouse
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import axiosInstance from '../../helpers/api';

interface SellerProfileModalProps {
  show: boolean;
  onHide: () => void;
  leadId: string;
  leadName: string;
  existingProfile?: any;
  onSave: () => void;
}

const SellerProfileModal: React.FC<SellerProfileModalProps> = ({
  show,
  onHide,
  leadId,
  leadName,
  existingProfile,
  onSave
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Property details
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    square_footage: '',
    lot_size_sqft: '',
    year_built: '',
    garage_spaces: '',
    pool: 'false',
    
    // Pricing information
    desired_price: '',
    estimated_equity: '',
    mortgage_balance: '',
    
    // Selling details
    selling_timeframe: '',
    reason_for_selling: '',
    motivation_level: 'medium',
    flexibility_level: 'moderate',
    occupancy_status: 'owner_occupied',
    property_condition: 'good',
    recent_updates: ''
  });

  useEffect(() => {
    console.log('🏠 SellerProfileModal - Props received:', {
      show,
      leadId,
      leadName,
      existingProfile
    });
    if (existingProfile) {
      console.log('📋 Setting form data from existing profile...');
      setFormData({
        property_address: existingProfile.property_address || '',
        property_city: existingProfile.property_city || '',
        property_state: existingProfile.property_state || '',
        property_zip: existingProfile.property_zip || '',
        property_type: existingProfile.property_type || '',
        bedrooms: existingProfile.bedrooms?.toString() || '',
        bathrooms: existingProfile.bathrooms?.toString() || '',
        square_footage: existingProfile.square_footage?.toString() || '',
        lot_size_sqft: existingProfile.lot_size_sqft?.toString() || '',
        year_built: existingProfile.year_built?.toString() || '',
        garage_spaces: existingProfile.garage_spaces?.toString() || '',
        pool: existingProfile.pool ? 'true' : 'false',
        desired_price: existingProfile.desired_price?.toString() || '',
        estimated_equity: existingProfile.estimated_equity?.toString() || '',
        mortgage_balance: existingProfile.mortgage_balance?.toString() || '',
        selling_timeframe: existingProfile.selling_timeframe || '',
        reason_for_selling: existingProfile.reason_for_selling || '',
        motivation_level: existingProfile.motivation_level || 'medium',
        flexibility_level: existingProfile.flexibility_level || 'moderate',
        occupancy_status: existingProfile.occupancy_status || 'owner_occupied',
        property_condition: existingProfile.property_condition || 'good',
        recent_updates: existingProfile.recent_updates || ''
      });
    }
  }, [existingProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Get user_id from localStorage (same as token)
      const userInfo = localStorage.getItem('user_info');
      const userId = userInfo ? JSON.parse(userInfo).id : null;
      
      const payload = {
        user_id: userId, // Add user_id to payload as the seller controller expects it
        property_address: formData.property_address || null,
        property_city: formData.property_city || null,
        property_state: formData.property_state || null,
        property_zip: formData.property_zip || null,
        property_type: formData.property_type || null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        lot_size_sqft: formData.lot_size_sqft ? parseInt(formData.lot_size_sqft) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        garage_spaces: formData.garage_spaces ? parseInt(formData.garage_spaces) : null,
        pool: formData.pool === 'true',
        desired_price: formData.desired_price ? parseInt(formData.desired_price) : null,
        estimated_equity: formData.estimated_equity ? parseInt(formData.estimated_equity) : null,
        mortgage_balance: formData.mortgage_balance ? parseInt(formData.mortgage_balance) : null,
        selling_timeframe: formData.selling_timeframe || null,
        reason_for_selling: formData.reason_for_selling || null,
        motivation_level: formData.motivation_level || 'medium',
        flexibility_level: formData.flexibility_level || 'moderate',
        occupancy_status: formData.occupancy_status || 'owner_occupied',
        property_condition: formData.property_condition || 'good',
        recent_updates: formData.recent_updates || null
      };

      let response;
      if (existingProfile?.id) {
        // Update existing profile using the existing seller API endpoint
        response = await axiosInstance.put(`/sellers/${leadId}/profile`, payload);
      } else {
        // Create new profile using the existing seller API endpoint
        response = await axiosInstance.post(`/sellers/${leadId}/profile`, payload);
      }

      if (response.data.status === 'success' || response.status === 200 || response.status === 201) {
        toast.success(`Seller profile ${existingProfile ? 'updated' : 'created'} successfully!`);
        onSave();
        onHide();
      } else {
        toast.error('Failed to save seller profile');
      }
    } catch (error: any) {
      console.error('Error saving seller profile:', error);
      toast.error(error.response?.data?.message || 'Failed to save seller profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      style={{ zIndex: 9999 }}
      backdrop="static"
      backdropClassName="high-z-backdrop"
    >
      <Modal.Header closeButton className="bg-warning text-dark">
        <Modal.Title>
          <FontAwesomeIcon icon={faDollarSign} className="me-2" />
          {existingProfile ? 'Edit' : 'Create'} Seller Profile - {leadName}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            <strong>Complete Seller Profile</strong>
            <p className="mb-0 mt-1">
              This information will help you provide accurate property valuations and marketing strategies.
            </p>
          </Alert>

          {/* Property Details Section */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faHome} className="me-2 text-warning" />
            Property Details
          </h5>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Property Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="123 Main Street"
                  value={formData.property_address}
                  onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Austin"
                  value={formData.property_city}
                  onChange={(e) => setFormData({...formData, property_city: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="TX"
                  maxLength={2}
                  value={formData.property_state}
                  onChange={(e) => setFormData({...formData, property_state: e.target.value.toUpperCase()})}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>ZIP Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="78701"
                  value={formData.property_zip}
                  onChange={(e) => setFormData({...formData, property_zip: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Property Type</Form.Label>
                <Form.Select
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                >
                  <option value="">Select Type</option>
                  <option value="single_family">Single Family Home</option>
                  <option value="condo">Condominium</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                  <option value="land">Land/Lot</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faBed} className="me-1" />
                  Bedrooms
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 3"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faBath} className="me-1" />
                  Bathrooms
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 2"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faRuler} className="me-1" />
                  Square Footage
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 2500"
                  value={formData.square_footage}
                  onChange={(e) => setFormData({...formData, square_footage: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Year Built</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 1995"
                  value={formData.year_built}
                  onChange={(e) => setFormData({...formData, year_built: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faWarehouse} className="me-1" />
                  Garage Spaces
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 2"
                  value={formData.garage_spaces}
                  onChange={(e) => setFormData({...formData, garage_spaces: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faSwimmer} className="me-1" />
                  Pool?
                </Form.Label>
                <Form.Select
                  value={formData.pool}
                  onChange={(e) => setFormData({...formData, pool: e.target.value})}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faKey} className="me-1" />
                  Occupancy Status
                </Form.Label>
                <Form.Select
                  value={formData.occupancy_status}
                  onChange={(e) => setFormData({...formData, occupancy_status: e.target.value})}
                >
                  <option value="owner_occupied">Owner Occupied</option>
                  <option value="tenant_occupied">Tenant Occupied</option>
                  <option value="vacant">Vacant</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Pricing Information */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faDollarSign} className="me-2 text-warning" />
            Pricing Information
          </h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Desired Sale Price</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 450000"
                  value={formData.desired_price}
                  onChange={(e) => setFormData({...formData, desired_price: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Estimated Equity</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 200000"
                  value={formData.estimated_equity}
                  onChange={(e) => setFormData({...formData, estimated_equity: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Mortgage Balance</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 250000"
                  value={formData.mortgage_balance}
                  onChange={(e) => setFormData({...formData, mortgage_balance: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Selling Timeline & Motivation */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-warning" />
            Timeline & Motivation
          </h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Selling Timeline</Form.Label>
                <Form.Select
                  value={formData.selling_timeframe}
                  onChange={(e) => setFormData({...formData, selling_timeframe: e.target.value})}
                >
                  <option value="">Select Timeline</option>
                  <option value="within_1_month">Within 1 month</option>
                  <option value="within_3_months">Within 3 months</option>
                  <option value="within_6_months">Within 6 months</option>
                  <option value="within_1_year">Within 1 year</option>
                  <option value="flexible">Flexible</option>
                  <option value="not_specified">Not specified</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faFire} className="me-1" />
                  Motivation Level
                </Form.Label>
                <Form.Select
                  value={formData.motivation_level}
                  onChange={(e) => setFormData({...formData, motivation_level: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High - Need to sell quickly</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Price Flexibility</Form.Label>
                <Form.Select
                  value={formData.flexibility_level}
                  onChange={(e) => setFormData({...formData, flexibility_level: e.target.value})}
                >
                  <option value="rigid">Not flexible</option>
                  <option value="moderate">Moderate</option>
                  <option value="flexible">Flexible</option>
                  <option value="very_flexible">Very flexible</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Reason for Selling</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., Relocating for work, downsizing, upgrading..."
                  value={formData.reason_for_selling}
                  onChange={(e) => setFormData({...formData, reason_for_selling: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Recent Updates/Improvements</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., New roof (2023), Updated kitchen..."
                  value={formData.recent_updates}
                  onChange={(e) => setFormData({...formData, recent_updates: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Property Condition</Form.Label>
                <Form.Select
                  value={formData.property_condition}
                  onChange={(e) => setFormData({...formData, property_condition: e.target.value})}
                >
                  <option value="excellent">Excellent - Move-in ready</option>
                  <option value="good">Good - Minor updates needed</option>
                  <option value="fair">Fair - Some repairs needed</option>
                  <option value="poor">Poor - Major repairs needed</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="warning" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save Seller Profile
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SellerProfileModal;